import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AUDIT_ANSWER,
  AUDIT_SECTIONS,
  HOW_STEPS,
  calculateAuditScore,
  getDefaultAnswers,
} from './targetAuditLogic';
import './TargetAudit.css';

const ANSWER_LABELS = {
  yes: 'Да',
  partial: 'Частично',
  no: 'Нет',
};

const ANSWER_OPTIONS = [AUDIT_ANSWER.YES, AUDIT_ANSWER.PARTIAL, AUDIT_ANSWER.NO];

const TargetAudit = () => {
  const [answers, setAnswers] = useState(getDefaultAnswers);
  const resultsRef = useRef(null);
  const wasCompleteRef = useRef(false);

  const result = useMemo(() => calculateAuditScore(answers), [answers]);

  const setAnswer = (itemId, value) => {
    setAnswers((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleReset = () => {
    setAnswers(getDefaultAnswers());
    wasCompleteRef.current = false;
  };

  useEffect(() => {
    if (result.isComplete && !wasCompleteRef.current) {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    wasCompleteRef.current = result.isComplete;
  }, [result.isComplete]);

  return (
    <div className="ta-tool">
      <div className="ta-toolbar">
        <span className="ta-progress">
          {`Отвечено: ${result.answeredCount} из ${result.totalCount}`}
        </span>
        <button type="button" className="ta-reset-btn" onClick={handleReset}>
          {'Сбросить всё'}
        </button>
      </div>

      {AUDIT_SECTIONS.map((section) => (
        <div key={section.id} className="ta-section">
          <div className="ta-panel-title">{section.title}</div>
          <div className="ta-items">
            {section.items.map((item) => (
              <div key={item.id} className="ta-item">
                <div className="ta-item-text">
                  <div className="ta-item-title">{item.title}</div>
                  <div className="ta-item-hint">{item.hint}</div>
                </div>
                <div className="ta-answers" role="group" aria-label={item.title}>
                  {ANSWER_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`ta-answer-btn${answers[item.id] === option ? ' ta-answer-btn--active' : ''} ta-answer-btn--${option}`}
                      onClick={() => setAnswer(item.id, option)}
                    >
                      {ANSWER_LABELS[option]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {result.answeredCount > 0 ? (
        <div ref={resultsRef} className="ta-results">
          <div className="ta-score-panel">
            <div className="ta-score-main">
              <div className="ta-score-label">{'Готовность к запуску'}</div>
              <div className="ta-score-value">{result.percent}%</div>
              <div className="ta-score-bar">
                <div className="ta-score-bar-fill" style={{ width: `${result.percent}%` }} />
              </div>
              <p className="ta-score-status">{result.status}</p>
            </div>
            <p className="ta-score-hint">{'Ответьте на все пункты. «Частично» = 50% балла за пункт.'}</p>
          </div>

          {result.isComplete && result.topGaps.length > 0 ? (
            <div className="ta-gaps-panel">
              <div className="ta-panel-title">{'Сделать в первую очередь'}</div>
              <ul className="ta-gaps-list">
                {result.topGaps.map((gap) => (
                  <li key={gap.id}>{gap.title}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.isComplete && result.topGaps.length === 0 ? (
            <div className="ta-gaps-panel ta-gaps-panel--ok">
              <p>{'Критичных пробелов нет — можно переходить к тестовому запуску.'}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="ta-how">
        <div className="ta-panel-title">{'Как считается'}</div>
        {HOW_STEPS.map((step) => (
          <div key={step.num} className="ta-how-step">
            <div className="ta-how-num">{step.num}</div>
            <div className="ta-how-text">
              <strong>{step.title}</strong> {step.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TargetAudit;
